import type { ReactNode } from 'react';
import { FileTextIcon } from 'lucide-react';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, LoadingSpinner, Pagination, QueryErrorAlert } from '@shared/components';
import type { GeneratedDocumentsPage } from '../../types/agreementDocuments.types';

export type DocumentsListStatus = 'loading' | 'error' | 'ready';

interface Props {
    status: DocumentsListStatus;
    page: GeneratedDocumentsPage | undefined;
    currentPage: number;
    canGenerate: boolean;
    hasStillGenerating: boolean;
    onGenerate: () => void;
    onRetry: () => void;
    onRefresh: () => void;
    onPageChange: (page: number) => void;
    children: ReactNode;
}

const headingId = 'agreement-documents-heading';

export function DocumentsListCard(props: Props) {
    const { page, currentPage, canGenerate, onGenerate, onPageChange } = props;

    return (
        <Card ariaLabelledBy={headingId}>
            <CardHeader className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <CardTitle id={headingId}>Documents</CardTitle>
                    <p className="mt-1 text-sm text-mp-muted">Documents generated for this agreement, newest first.</p>
                </div>
                {canGenerate && (
                    <Button type="button" size="sm" variant="primary" onClick={onGenerate}>
                        Generate document
                    </Button>
                )}
            </CardHeader>
            <ListBody {...props} />
            {props.status === 'ready' && page && page.meta.lastPage > 1 && (
                <CardContent>
                    <Pagination
                        currentPage={currentPage}
                        lastPage={page.meta.lastPage}
                        total={page.meta.total}
                        perPage={page.meta.perPage}
                        onPageChange={onPageChange}
                    />
                </CardContent>
            )}
        </Card>
    );
}

function ListBody({ status, page, hasStillGenerating, onRetry, onRefresh, children }: Props) {
    if (status === 'loading') {
        return (
            <CardContent>
                <LoadingSpinner message="Loading documents" />
            </CardContent>
        );
    }

    if (status === 'error' || !page) {
        return (
            <CardContent>
                <QueryErrorAlert message="Failed to load the agreement's documents." onRetry={onRetry} />
            </CardContent>
        );
    }

    if (page.data.length === 0) {
        return (
            <CardContent>
                <EmptyState
                    icon={FileTextIcon}
                    title="No documents yet"
                    description="No documents have been generated for this agreement."
                />
            </CardContent>
        );
    }

    return (
        <>
            {hasStillGenerating && (
                <CardContent>
                    <Alert variant="info" role="status" className="flex flex-wrap items-center justify-between gap-3">
                        <span>A document is taking longer than usual to generate. The list stops checking automatically.</span>
                        <Button
                            type="button"
                            size="xs"
                            variant="info"
                            appearance="outline"
                            className="shrink-0 whitespace-nowrap"
                            onClick={onRefresh}
                        >
                            Refresh
                        </Button>
                    </Alert>
                </CardContent>
            )}
            {children}
        </>
    );
}
